import { recommendPlan, resolveRemindersPausedReason } from '@shared/core/billing'
import type { BillingPlanListType } from '@shared/core/billing'
import { useEffect, useMemo, useState } from 'react'
import { useNotSentRemindersCount } from 'src/modules/event/hooks/use-not-sent-reminders-count'
import { billingTypes } from 'src/modules/payment/constants/payment.constants'
import { useFreePlan } from 'src/modules/payment/hooks/use-free-plan'
import { usePlanData } from 'src/modules/payment/hooks/use-plan-data'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
import type { PlansAccount } from 'src/modules/payment/types/plans-offer.types'
import {
  formatSubscriptionCancellationDisplay,
  isShopifyBilledProfile,
  parseBillingPausedAt,
} from 'src/modules/payment/utils/helpers'
import { getReminderCycleWindow, getUpgradeAccountState } from 'src/modules/payment/utils/plans-offer.utils'
import { useProfile } from 'src/modules/user/hooks/use-profile'

/** Everything the plans content needs to render for the signed-in account. */
export function usePlansOffer(plansType: BillingPlanListType) {
  const [isYearly, setIsYearly] = useState(true)
  const profile = useProfile()
  const subscriptionOptions = {
    enabled: !!profile.data?.billing_subscription_id || isShopifyBilledProfile(profile.data),
  }
  const subscription = useSubscription({}, subscriptionOptions)
  const freePlan = useFreePlan()
  const planData = usePlanData({ isYearly, subscriptionOptions, type: plansType })
  const { currentPlan, availablePlans, filteredPlans } = planData
  const isFreeTrial = !!currentPlan && currentPlan.name === freePlan.data?.name
  const isPaused = isFreeTrial && !!profile.data?.reminders_paused
  const isRunningLow = isFreeTrial && !!profile.data?.reminders_running_low
  const pausedReason = profile.data ? resolveRemindersPausedReason(profile.data) : null
  const notSentCount = useNotSentRemindersCount({ enabled: isPaused })
  const isLoading = subscription.isLoading || freePlan.isLoading || planData.loading

  // Paying accounts open on their own interval so their card still reads "Current plan".
  useEffect(() => {
    setIsYearly(isFreeTrial || currentPlan?.billing_type === billingTypes.longInterval)
  }, [currentPlan?.billing_type, isFreeTrial])

  const account = useMemo<PlansAccount | null>(
    () =>
      isLoading || !profile.data || !currentPlan
        ? null
        : {
            isFreeTrial,
            isPaused,
            isRunningLow,
            pausedReason,
            accountState: getUpgradeAccountState({ isFreeTrial, isPaused, pausedReason }),
          },
    [currentPlan, isFreeTrial, isLoading, isPaused, isRunningLow, pausedReason, profile.data],
  )

  const recommendedPlan = useMemo(() => {
    const now = new Date()
    const cycle = getReminderCycleWindow(profile.data, now)

    return recommendPlan({
      plans: availablePlans,
      currentPlan: isFreeTrial ? null : currentPlan,
      sentCount: profile.data?.reminders_sent_during_billing_cycle ?? 0,
      cycleStart: cycle.start,
      cycleEnd: cycle.end,
      now,
    })
  }, [availablePlans, currentPlan, isFreeTrial, profile.data])

  return {
    profile: profile.data,
    subscription: subscription.data,
    currentPlan,
    availablePlans,
    filteredPlans,
    account,
    isLoading,
    isYearly,
    setIsYearly,
    notSentCount: notSentCount.data?.not_sent_count,
    recommendedPlanId: recommendedPlan?.paddle_id,
    subscriptionEndedAt: profile.data ? formatSubscriptionCancellationDisplay(profile.data) : null,
    billingPausedAt: profile.data ? parseBillingPausedAt(profile.data) : null,
  }
}
